import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';

function Login() {
    const { login, authFetch } = useContext(AppContext);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [username, setUsername]   = useState('');
    const [password, setPassword]   = useState('');
    const [showPass, setShowPass]   = useState(false);
    const [error, setError]         = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const res  = await authFetch('/api/auth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            const data = await res.json();
            if (res.ok && data.token && data.user) {
                login(data.token, data.user);
                navigate('/');
            } else {
                setError(data.message || data.error || t('Login failed. Please check your credentials.'));
            }
        } catch {
            setError(t('Network error. Unable to connect to the server.'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex h-screen w-full font-sans bg-erp-bg overflow-hidden">

            {/* ── Left panel — warm brand panel ───────────────────────── */}
            <div className="hidden lg:flex w-[52%] bg-erp-sidebar flex-col justify-between p-12 relative overflow-hidden">

                {/* Subtle texture dots — very minimal */}
                <div className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                        backgroundSize: '28px 28px',
                    }}
                />

                {/* Accent bar top-left */}
                <div className="absolute top-0 left-0 w-1 h-40 bg-[#5b6af0] rounded-r-full opacity-80" />

                {/* Logo */}
                <div className="relative flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#5b6af0] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-base">R</span>
                    </div>
                    <div>
                        <h1 className="text-white font-bold text-lg leading-none tracking-tight">
                            Royal<span className="text-[#9ba5f7]">Pips</span>
                        </h1>
                        <p className="text-slate-500 text-[10px] font-medium mt-0.5 tracking-wide">ERP System</p>
                    </div>
                </div>

                {/* Main copy */}
                <div className="relative">
                    <p className="text-[#9ba5f7] text-sm font-semibold uppercase tracking-widest mb-4">
                        Business Management Platform
                    </p>
                    <h2 className="text-white text-4xl font-bold leading-tight mb-5" style={{ letterSpacing: '-0.02em' }}>
                        Everything your<br />
                        business needs,<br />
                        in one place.
                    </h2>
                    <p className="text-slate-400 text-base leading-relaxed max-w-sm">
                        Track inventory, manage sales, handle procurement and analyze your business — all from a single dashboard built for real work.
                    </p>

                    {/* Feature list */}
                    <ul className="mt-8 space-y-3">
                        {[
                            'Point of Sale & Sales Tracking',
                            'Inventory & Warehouse Management',
                            'Accounting & Financial Reports',
                            'Multi-Branch Operations',
                        ].map(feat => (
                            <li key={feat} className="flex items-center gap-3 text-slate-400 text-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#5b6af0] flex-shrink-0" />
                                {feat}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Footer info */}
                <div className="relative flex items-center gap-4">
                    <div className="h-px flex-1 bg-erp-sidebar-border" />
                    <span className="text-slate-600 text-xs font-medium">Secured · Reliable · Fast</span>
                    <div className="h-px flex-1 bg-erp-sidebar-border" />
                </div>
            </div>

            {/* ── Right panel — login form ─────────────────────────────── */}
            <div className="flex-1 flex flex-col items-center justify-center bg-white px-8 py-12 relative">

                {/* Mobile logo */}
                <div className="lg:hidden absolute top-6 left-6 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-[#5b6af0] flex items-center justify-center">
                        <span className="text-white font-bold text-xs">R</span>
                    </div>
                    <span className="text-erp-text font-bold text-base tracking-tight">RoyalPips</span>
                </div>

                <div className="w-full max-w-[380px]">

                    {/* Heading */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-erp-text mb-1">{t('Welcome back')}</h2>
                        <p className="text-erp-text-3 text-sm">{t('Please enter your details to sign in.')}</p>
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="mb-5 flex items-start gap-3 bg-erp-danger-bg border border-red-200 rounded-erp px-4 py-3">
                            <svg className="w-4 h-4 text-erp-danger mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm text-erp-danger">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleLogin} className="space-y-5">

                        {/* Username */}
                        <div>
                            <label className="erp-label" htmlFor="username">{t('Username')}</label>
                            <input
                                id="username"
                                type="text"
                                required
                                autoComplete="username"
                                className="erp-input"
                                placeholder={t('Enter your username')}
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label className="erp-label" htmlFor="password">{t('Password')}</label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPass ? 'text' : 'password'}
                                    required
                                    autoComplete="current-password"
                                    className="erp-input pr-10"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPass(v => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-erp-text-3 hover:text-erp-text-2 transition-colors"
                                    tabIndex={-1}
                                    aria-label={showPass ? 'Hide password' : 'Show password'}
                                >
                                    {showPass ? (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Remember / Forgot */}
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    id="remember-me"
                                    type="checkbox"
                                    className="w-4 h-4 rounded border-erp-border text-[#5b6af0] focus:ring-[#5b6af0]/30 cursor-pointer"
                                />
                                <span className="text-sm text-erp-text-2">{t('Remember me')}</span>
                            </label>
                            <a href="#" className="text-sm font-medium text-[#5b6af0] hover:text-[#4a58e8] transition-colors">
                                {t('Forgot password?')}
                            </a>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 bg-[#5b6af0] hover:bg-[#4a58e8] text-white text-sm font-semibold py-3 px-4 rounded-erp transition-colors active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    {t('Signing in…')}
                                </>
                            ) : t('Sign in')}
                        </button>
                    </form>

                    {/* Demo credentials */}
                    <div className="mt-8 pt-6 border-t border-erp-border">
                        <p className="text-xs font-semibold text-erp-text-3 uppercase tracking-widest text-center mb-3">{t('Demo Credentials')}</p>
                        <div className="flex justify-center gap-3 flex-wrap">
                            {['admin / admin123', 'chef / chef123'].map(cred => (
                                <span key={cred} className="bg-erp-bg text-erp-text-2 text-xs px-3 py-1.5 rounded-erp border border-erp-border font-mono">
                                    {cred}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;